import DOMPurify from 'dompurify';

const STOPWORDS = new Set([
  'i', 'w', 'na', 'do', 'z', 'za', 'u', 'o', 'od', 'po', 'nad', 'pod', 'przy', 'oraz', 'lub', 'albo', 'że', 'to', 'te', 'ta', 'ten',
  'a', 'an', 'the', 'and', 'or', 'of', 'to', 'in', 'on', 'for', 'by', 'at', 'with', 'from', 'as',
]);

const MIN_TOKEN_LEN = 3;
const MAX_EDITS = 1;

function stripDiacritics(s: string) {
  return s.normalize('NFKD').replace(/\p{M}/gu, '');
}

function normalizeWithMap(src: string): { norm: string; map: number[] } {
  const map: number[] = [];
  let norm = '';
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    const base = stripDiacritics(ch);
    if (base.length === 0) continue;
    norm += base;
    map.push(i);
  }
  return { norm, map };
}

function tokenize(q: string): string[] {
  const phrases: string[] = [];
  q = q.replace(/"([^"]+)"/g, (_, p1) => {
    phrases.push(p1.trim());
    return ' ';
  });
  const words = q.split(/\s+/).filter(Boolean);
  const raw = [...phrases, ...words].map((t) => t.trim().toLowerCase());
  const cleaned = raw
    .map((t) => stripDiacritics(t))
    .map((t) => t.replace(/[^\p{L}\p{N}\-_.]/gu, ''))
    .filter((t) => t && t.length >= MIN_TOKEN_LEN && !STOPWORDS.has(t));
  return Array.from(new Set(cleaned));
}

function isEditsLeq1(a: string, b: string): boolean {
  const la = a.length;
  const lb = b.length;
  const diff = Math.abs(la - lb);
  if (diff > MAX_EDITS) return false;
  if (la > lb) return isEditsLeq1(b, a);

  let i = 0;
  let j = 0;
  let edits = 0;
  while (i < la && j < lb) {
    if (a[i] === b[j]) {
      i++;
      j++;
      continue;
    }
    if (++edits > MAX_EDITS) return false;
    if (la === lb) {
      i++;
      j++;
    } else {
      j++;
    }
  }

  if (j < lb || i < la) edits++;
  return edits <= MAX_EDITS;
}

function findHighlightRangesFuzzy(title: string, tokens: string[]): Array<[number, number]> {
  const { norm, map } = normalizeWithMap(title.toLowerCase());
  const ranges: Array<[number, number]> = [];

  for (const tk of tokens) {
    if (!tk) continue;
    const L = tk.length;
    const lengthsToTry = Array.from(new Set([Math.max(1, L - 1), L, Math.min(L + 1, norm.length)]));

    for (const wLen of lengthsToTry) {
      for (let start = 0; start + wLen <= norm.length; start++) {
        const window = norm.slice(start, start + wLen);
        if (!isEditsLeq1(tk, window)) continue;

        const startOrig = map[start];
        const endOrig = map[start + wLen - 1] + 1;
        ranges.push([startOrig, endOrig]);

        start += Math.max(1, Math.floor(wLen / 2));
      }
    }
  }

  ranges.sort((a, b) => a[0] - b[0]);
  const merged: Array<[number, number]> = [];
  for (const r of ranges) {
    if (!merged.length || r[0] > merged[merged.length - 1][1]) {
      merged.push(r);
    } else {
      merged[merged.length - 1][1] = Math.max(merged[merged.length - 1][1], r[1]);
    }
  }
  return merged;
}

export function highlightText(title: string, query: string, highlightClass: string): string {
  const sanitizedTitle = DOMPurify.sanitize(title ?? '');
  const sanitizedQuery = DOMPurify.sanitize(query ?? '').trim();
  if (!sanitizedQuery) return sanitizedTitle;

  const tokens = tokenize(sanitizedQuery);
  if (tokens.length === 0) return sanitizedTitle;

  const ranges = findHighlightRangesFuzzy(sanitizedTitle, tokens);
  if (ranges.length === 0) return sanitizedTitle;

  let out = '';
  let cursor = 0;
  for (const [s, e] of ranges) {
    if (cursor < s) out += sanitizedTitle.slice(cursor, s);
    out += `<mark class="${highlightClass}">` + sanitizedTitle.slice(s, e) + '</mark>';
    cursor = e;
  }
  if (cursor < sanitizedTitle.length) out += sanitizedTitle.slice(cursor);
  return out;
}
